<template>
  <div class="basic-radio-input-container">
    <label class="radio-label">{{ label }} {{ validation.split('|').includes('required') ? '*' : '' }}</label>
    <Field
      v-slot="{ field }"
      :label="label"
      :name="name"
      :value="String(defaultValue)"
      :rules="validation"
    >
      <div
        class="radio-input-group"
        :style="{ display: displayInline ? 'flex' : 'block' }"
      >
        <label
          v-for="(option, index) in mutatedDataOptions"
          :key="index"
          class="radio-input"
          :style="{ width: displayInline ? 'auto' : '100%', display: displayInline ? 'inline-block' : 'block' }"
        >
          {{ getTranslations(option) }}
          <input
            :ref="name"
            type="radio"
            :checked="option.value === defaultValue"
            :disabled="option.disabled || isReadOnly"
            v-bind="field"
            :value="option.value !== null && option.value !== undefined ? String(option.value) : option"
            @change="onInput($event.target.value)"
          >
          <div class="radio-mask" />
        </label>
      </div>
      <ErrorMessage
        class="error-msg"
        :name="name"
      />
    </Field>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { store } from '@/stores/index.js'
import { Field, ErrorMessage } from 'vee-validate'
import { configurationService } from '@/services/configuration.service'
/**
 * Props:
 * name: input name (string)
 * label: Title displayed: e.g.:'Selectionner une option: ' (string),
 * displayInline: as true -> display options as inline, false -> display options as list (bool),
 * dataOptions: configurations needed for the options (Array with an object for each option) e.g.:
 *    [
 *      {
 *        value: value returned by the option (Number),
 *        label: label of the input (String)
 *        disabled: if you want to set the input as disabled (Bool)
 *      }
 *    ]
 */

export default {
  name: 'BasicRadioInput',
  components: { Field, ErrorMessage },
  props: {
    label: String,
    validation: [String, Boolean],
    name: String,
    defaultValue: {
      type: [String, Array, Boolean, Number, Object],
      default: undefined
    },
    displayInline: Boolean,
    dataOptions: Array,
    apiUrl: String,
    uniqueApiCall: {
      type: Boolean,
      default: false
    },
    valueField: String,
    labelFields: Array,
    isReadOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedDataOptions: this.dataOptions
    }
  },
  computed: {
    ...mapState(store, ['formApiResponses'])
  },
  mounted() {
    if (this.defaultValue !== undefined) {
      this.$emit('addData', { value: this.defaultValue, name: this.name })
    } else {
      this.$emit('addData', { value: '', name: this.name })
    }
    if (this.apiUrl) {
      let urlFormatted = this.apiUrl.replaceAll('/', '_')
      if (urlFormatted.includes('?')) {
        urlFormatted = urlFormatted.split('?')[0]
      }
      if (!this.uniqueApiCall) {
        // if uniqueApiCall is false, do the call everytime
        configurationService.getFormData(this.apiUrl, null).then(response => {
          this.mutatedDataOptions = response
        })
      } else if (!this.formApiResponses[urlFormatted]) {
        // if uniqueCall and result of api is not set, do the call and set it
        configurationService.getFormData(this.apiUrl, null).then(response => {
          this.mutatedDataOptions = response
          let data = {
            key: urlFormatted,
            value: this.mutatedDataOptions
          }
          this.$store.setFormApiResponses(data)
        })
      } else {
        // set the mutatedDataOption with data set in store
        this.mutatedDataOptions = this.formApiResponses[urlFormatted]
      }
    }
  },
  methods: {
    onInput(valueData) {
      const value = valueData === 'true' ? true : valueData === 'false' ? false : valueData
      this.$emit('addData', { value, name: this.name })
    },
    getTranslations(option) {
      if (option.label) {
        return this.$t('form.' + option.label)
      } else if (option[this.labelFields[0]]) {
        return this.$t('form.' + option[this.labelFields[0]])
      }
      return option[this.labelFields[0]]
    }
  }
}
</script>

<style lang="scss" scoped>
  .basic-radio-input-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    text-align: left;
    margin: 10px 0;

    /* Tablet (portrait) */
    @media only screen and (width >= 768px) {
      margin: 20px 0;
    }

    /* Desktops and laptops */
    @media only screen and (width >= 1024px) {
      margin: 20px 0;
    }

    /* Large screens */
    @media only screen and (width >= 1824px) {
      margin: 20px 0;
    }

    .radio-label {
      padding-left: calc(0.625em - 1px);
      padding-right: 5vw;
      font-weight: bolder;
      text-align: left;
      color: black;
    }

    .radio-input-group {
      width: auto;

      .radio-input {
        position: relative;
        margin-bottom: 5px;
        width: 100%;
        padding: 0 22px;
      }

      .radio-mask {
        position: absolute;
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        left: 1px;
        top: 1px;
        background: rgb(170 170 170);
        cursor: pointer;

        @media only screen and (width >= 768px) {
          width: 1.5rem;
          height: 1.5rem;
          left: -6px;
          top: -3px;
        }

        /* Desktops and laptops */
        @media only screen and (width >= 1024px) {
          width: 1.5rem;
          height: 1.5rem;
          left: -6px;
          top: -3px;
        }

        /* Large screens */
        @media only screen and (width >= 1824px) {
          width: 1.5rem;
          height: 1.5rem;
          left: -6px;
          top: -3px;
        }
      }
    }

    input[type='radio'] {
      visibility: hidden;
    }

    input[type='radio']:checked + .radio-mask {
      background-color: rgb(var(--custom-color-main));
    }

    input[type='radio']:disabled + .radio-mask {
      background-color: rgb(225 225 225);
    }

    input[type='radio']:disabled:checked + .radio-mask::before {
      background-color: rgb(150 150 150);
    }

    input[type='radio']:checked + .radio-mask::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: white;
      width: 35%;
      height: 35%;
      border-radius: 50%;
    }
  }
</style>
